<script lang="ts">
  import * as Card from "$lib/components/ui/card/index.js";
  import { t } from "svelte-i18n";
  import Input from "$lib/components/ui/input/input.svelte";
  import { isEditActive } from "$src/application/main/mainTemplateStore";
  import {
    addProjectSectionError,
    projectDraft,
    ProjectFormSection,
    projectOriginal,
    registerErrors,
  } from "$src/application/projects/stores/projectStore";
  import { Label } from "$lib/components/ui/label";
  import { StringUtils } from "$lib/stringUtils";
  import { Textarea } from "$lib/components/ui/textarea";
  import * as zod from "zod";
  import { createForm } from "felte";
  import { validateSchema, validator } from "@felte/validator-zod";
  import { onDestroy, onMount, tick } from "svelte";
  import { get } from "svelte/store";
  import VieweditCustomerCard from "$src/presentation/customers/components/viewedit-customer-card.svelte";
  import { type StoreSummary } from "$src/domain/directory/stores/StoreSummary";
  import SelectStore from "$src/presentation/groupstores/select-store.svelte";
  import SelectFolder from "$src/presentation/customers/components/select-folder.svelte";
  import { Checkbox } from "$lib/components/ui/checkbox";
  import VatInfo from "./vat-info.svelte";
  import { ScrollArea } from "$lib/components/ui/scroll-area/index.js";
  import { getTranslation } from "$src/presentation/core/TranslatedTextUtils";
  import { languageCode } from "$src/presentation/core/languageCode";
  import SelectProjectType from "$src/presentation/customers/components/select-project-type.svelte";
  import { Badge } from "$lib/components/ui/badge";

  export let groupStoreId: number;
  export let patchHandler: () => void;
  export let stores: StoreSummary[];

  let selectedStore: StoreSummary | undefined;

  const projectInfoSchema = zod.object({
    name: zod
      .string()
      .min(1, $t("page.projects.new.name-required"))
      .max(200, $t("page.projects.new.name-max-length")),
    folderName: zod
      .string()
      .max(50, $t("page.projects.new.foldername-max-length"))
      .optional(),
    globalComment: zod
      .string()
      .max(8000, $t("page.projects.detail.globalComment-max-length"))
      .optional(),
  });

  type FormValues = zod.infer<typeof projectInfoSchema>;

  $: editForm = createForm({
    initialValues: {},
    onSubmit: handleSubmit,
    extend: validator({ schema: projectInfoSchema }),
  });

  $: form = editForm.form;
  $: data = editForm.data;
  $: errors = editForm.errors;

  $: {
    registerErrors(ProjectFormSection.Info, $errors);
  }

  const editUnsubscribe = isEditActive.subscribe((isEditing) => {
    if (isEditing) {
      setTimeout(() => {
        editForm.validate();
      }, 100);
    }
  });

  onDestroy(() => {
    if (editUnsubscribe) editUnsubscribe();
  });

  function handleSubmit() {}

  function getStoreById(id: number): StoreSummary | undefined {
    return stores.find((s) => s.storeId == id);
  }
</script>

<Card.Root class="flex-auto min-w-[200px]">
  <Card.Header class="pb-4">
    <Card.Title class="flex flex-row justify-between">
      <div>{$t("page.projects.detail.info-title")}</div>
    </Card.Title>
  </Card.Header>
  <Card.Content>
    <form use:form>
      <div class="flex flex-col gap-1">
        <div class="flex flex-row gap-2 items-center font-bold">
          <Label class="text-xs font-normal text-muted-foreground ">
            {$t("page.projects.detail.info-status")} :
          </Label>
          <div>
            {getTranslation(
              $projectOriginal.currentStep?.translations,
              $languageCode
            )}
          </div>
        </div>
        <div class="flex flex-col">
          <Label class="text-xs font-normal text-muted-foreground ">
            {$t("page.projects.new.folder")}
          </Label>
          {#if $isEditActive && $projectDraft}
            <SelectFolder
              bind:value={$projectDraft.info.folderName}
              {groupStoreId}
              customerId={$projectDraft.customer.id}
            />
            {#if $errors.folderName}
              <div class="col-span-4 text-right text-xs text-red-500">
                {$errors.folderName}
              </div>
            {/if}
          {:else}
            <Label class="font-normal p-1">
              {$projectOriginal.info.folderName ??
                $t("page.projects.detail.defaultFolder")}
            </Label>
          {/if}
        </div>
        <div class="flex flex-col">
          <Label class="text-xs font-normal text-muted-foreground ">
            {$t("page.projects.new.name")}
          </Label>
          {#if $isEditActive && $projectDraft}
            <Input
              bind:value={$projectDraft.info.name}
              name="name"
              on:input={patchHandler}
              class={$errors.name ? "border-red-500" : ""}
            />
            {#if $errors.name}
              <div class="col-span-4 text-right text-xs text-red-500">
                {$errors.name}
              </div>
            {/if}
          {:else}
            <Label class="font-normal p-1">
              {$projectOriginal.info.name}
            </Label>
          {/if}
        </div>
        <div class="flex flex-col">
          <Label class="text-xs font-normal text-muted-foreground ">
            {$t("page.projects.new.folder-type")}
          </Label>
          {#if $isEditActive && $projectDraft}
            <SelectProjectType
              bind:value={$projectDraft.info.projectType}
              {groupStoreId}
            />
            {#if $errors.folderName}
              <div class="col-span-4 text-right text-xs text-red-500">
                {$errors.folderName}
              </div>
            {/if}
          {:else if $projectOriginal.info.projectType}
            <div class="p-1">
              <Badge variant="secondary">
                {getTranslation(
                  $projectOriginal.info.projectType.translations,
                  $languageCode
                )}
              </Badge>
            </div>
          {/if}
        </div>
        {#if stores.length > 1}
          <div class="col-span-full flex gap-0 items-center flex-wrap">
            <Label class="text-xs font-normal text-muted-foreground ">
              {$t("page.projects.new.store")}
            </Label>
            {#if $isEditActive && $projectDraft}
              {#if stores.length > 1}
                <SelectStore bind:value={selectedStore} />
              {:else}
                <Label class="w-full p-2 font-normal">
                  {$projectDraft.info.store.name}
                </Label>
              {/if}
              {#if $errors.storeId}
                <div class="col-span-4 text-right text-xs text-red-500">
                  {$errors.storeId}
                </div>
              {/if}
            {:else}
              <Label class="w-full p-2 font-normal">
                {$projectOriginal.info.store?.name}
              </Label>
            {/if}
          </div>
        {/if}
        <div class="col-span-full flex gap-0 items-center flex-wrap">
          {#if $isEditActive && $projectDraft}
            <Label class="text-xs font-normal text-muted-foreground ">
              {$t("page.projects.detail.comments")}
            </Label>
            <Textarea
              maxlength={8000}
              class="resize-none h-36 {$errors.globalComment
                ? 'border-red-500'
                : ''}"
              on:input={patchHandler}
              bind:value={$projectDraft.info.globalComment}
            />
            {#if $errors.globalComment}
              <div class="col-span-4 text-right text-xs text-red-500">
                {$errors.globalComment}
              </div>
            {/if}
          {:else if $projectOriginal.info.globalComment}
            <Label class="text-xs font-normal text-muted-foreground ">
              {$t("page.projects.detail.comments")}
            </Label>
            <div class="w-full">
              <ScrollArea class="h-36 ">
                <div class="p-2">
                  <Label class="w-full p-2 font-normal whitespace-pre-wrap">
                    {#if StringUtils.isNullOrEmpty($projectOriginal.info.globalComment)}
                      /
                    {:else}
                      {$projectOriginal.info.globalComment}
                    {/if}
                  </Label>
                </div>
              </ScrollArea>
            </div>
          {/if}
        </div>
        <div class="col-span-full flex gap-2 items-center flex-wrap">
          {#if $isEditActive && $projectDraft && $projectDraft.info.store.rules.displayHouseCondition}
            <div class="flex items-center space-x-2">
              <Checkbox
                id="houseCondition"
                bind:checked={$projectDraft.info.houseAged}
              />
              <Label
                for="houseCondition"
                class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {$t(
                  "page.projects.detail.houseRules.condition." +
                    $projectDraft.info.store.countryCode
                )}
              </Label>
            </div>
          {:else}
            <div class="flex items-center space-x-2">
              {#if $projectOriginal.info.houseAged}
                <Label
                  for="houseCondition"
                  class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {$t(
                    "page.projects.detail.houseRules.condition." +
                      $projectOriginal.info.store.countryCode
                  )}
                </Label>
              {/if}
            </div>
          {/if}
        </div>
      </div>
    </form>
  </Card.Content>
</Card.Root>
